import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {
  useActivateServiceAccountMutation,
  useDeactivateServiceAccountMutation,
  useDeleteServiceAccountMutation,
} from '@dms/api/security';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, noop, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const SERVICE_ACCOUNT_GRID_CODE = 'service-account';
const PREDEFINED_COLUMNS = ['name'] as const;

export function ServiceAccountOverview() {
  const navigate = useNavigate();
  const [activateServiceAccount] = useActivateServiceAccountMutation();
  const [deactivateServiceAccount] = useDeactivateServiceAccountMutation();
  const [deleteServiceAccount] = useDeleteServiceAccountMutation();

  const navigateToDetail = (serviceAccountId: string) =>
    navigate(
      composePath(settingsRoutes.serviceAccountManagementDetail, {params: {serviceAccountId}})
    );

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    refreshData,
    getColumnData,
  }) => {
    const serviceAccountId = isArray(rowId) ? rowId[0] : rowId;
    const serviceAccountName = getColumnData<string>('name')?.value;

    match(actionKey)
      .with('detail', 'edit', () => navigateToDetail(serviceAccountId))
      .with('activate', () =>
        activateServiceAccount({serviceAccountId})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .with('deactivate', () =>
        deactivateServiceAccount({serviceAccountId})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .with('delete', () =>
        openDeleteDialog({
          text: i18n.t('entity.serviceAccount.labels.deleteDialog', {name: serviceAccountName}),
          onConfirm: () =>
            deleteServiceAccount({serviceAccountId})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
          'data-testid': testIds.settings.serviceAccountManagement('deleteDialog'),
        })
      )
      .otherwise(noop);
  };

  return (
    <SettingsTemplate
      header={{
        title: i18n.t('entity.serviceAccount.labels.serviceAccounts'),
        subtitle: i18n.t('entity.serviceAccount.labels.serviceAccountsSubtitle'),
        actions: [
          {
            type: 'button',
            title: i18n.t('entity.serviceAccount.actions.newServiceAccount'),
            onClick: () => navigate(settingsRoutes.serviceAccountManagementCreate),
          },
        ],
      }}
      data-testid={testIds.settings.serviceAccountManagement('page')}
    >
      <DmsDataGrid
        gridCode={SERVICE_ACCOUNT_GRID_CODE}
        actionCallback={actionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        data-testid={testIds.settings.serviceAccountManagement('datagrid')}
      />
    </SettingsTemplate>
  );
}
